package com.sddamico.components.widgets

import androidx.compose.runtime.*
import com.sddamico.SitePalette
import com.sddamico.components.motion.Durations
import com.sddamico.components.motion.Ease
import com.sddamico.components.motion.PressTransition
import com.sddamico.components.motion.PressableStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/**
 * A button that shows a confirmation bar before executing.
 *
 * When confirming, renders an absolutely-positioned bar over the nearest
 * `position: relative` ancestor. Callers should set `position(Position.Relative)`
 * on the parent container (e.g. the button Row).
 */
@Composable
fun ConfirmableButton(
    label: String,
    loadingLabel: String,
    confirmLabel: String,
    isLoading: Boolean,
    enabled: Boolean = true,
    buttonModifier: Modifier,
    onConfirm: () -> Unit,
) {
    var confirming by remember { mutableStateOf(false) }
    var interactable by remember { mutableStateOf(false) }
    var barVisible by remember { mutableStateOf(false) }

    // Touch-target delay: after bar appears, wait 300ms before allowing clicks
    LaunchedEffect(confirming) {
        if (confirming) {
            barVisible = true
            delay(300)
            interactable = true
        } else {
            barVisible = false
            interactable = false
        }
    }

    // Always render the button (holds layout space)
    Div(
        PressableStyle.toModifier()
            .then(buttonModifier)
            .transition(Transition.of("background-color", Durations.HOVER.ms, Ease.Out.fn), PressTransition)
            .cursor(if (enabled && !isLoading) Cursor.Pointer else Cursor.Default)
            .onClick {
                if (isLoading || !enabled) return@onClick
                confirming = true
            }
            .toAttrs()
    ) {
        Text(if (isLoading) loadingLabel else label)
    }

    // Confirmation overlay — absolutely positioned over parent (needs position: relative on parent)
    if (confirming && !isLoading) {
        Div(
            Modifier
                .position(Position.Absolute)
                .top((-2).px).left((-2).px).right((-2).px).bottom((-2).px)
                .backgroundColor(SitePalette.surface)
                .border(1.px, LineStyle.Solid, SitePalette.border)
                .borderRadius(8.px)
                .display(DisplayStyle.Flex)
                .flexDirection(FlexDirection.Row)
                .alignItems(AlignItems.Center)
                .padding(topBottom = 0.5.cssRem, leftRight = 0.75.cssRem)
                .styleModifier { property("box-sizing", "border-box") }
                .gap(0.75.cssRem)
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property("opacity", if (barVisible) "1" else "0")
                    property("transition", "opacity ${Durations.HOVER}ms ${Ease.Out.css}")
                }
                .toAttrs {
                    attr("tabindex", "0")
                    addEventListener("keydown") { evt ->
                        val key = evt.nativeEvent.asDynamic().key as? String
                        if (key == "Escape") {
                            // The surrounding Modal also closes on Escape; backing out of the confirm shouldn't.
                            evt.stopPropagation()
                            confirming = false
                        }
                    }
                    ref { element ->
                        element.focus()
                        onDispose {}
                    }
                }
        ) {
            // Confirm text
            SpanText(
                confirmLabel,
                Modifier
                    .fontSize(0.8125.cssRem)
                    .fontWeight(FontWeight.Medium)
                    .color(SitePalette.text)
                    .styleModifier { property("min-width", "0") }
            )

            // Spacer
            Div(Modifier.flexGrow(1).toAttrs())

            // Yes button
            Div(
                Modifier
                    .backgroundColor(if (interactable) SitePalette.primary else SitePalette.border)
                    .color(Colors.White)
                    .borderRadius(4.px)
                    .padding(topBottom = 0.25.cssRem, leftRight = 0.625.cssRem)
                    .fontSize(0.75.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .cursor(if (interactable) Cursor.Pointer else Cursor.Default)
                    .transition(Transition.of("background-color", 150.ms))
                    .onClick {
                        if (!interactable) return@onClick
                        confirming = false
                        onConfirm()
                    }
                    .toAttrs()
            ) {
                Text("Yes")
            }

            // No button
            Div(
                Modifier
                    .backgroundColor(if (interactable) SitePalette.error else SitePalette.border)
                    .color(Colors.White)
                    .borderRadius(4.px)
                    .padding(topBottom = 0.25.cssRem, leftRight = 0.625.cssRem)
                    .fontSize(0.75.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .cursor(if (interactable) Cursor.Pointer else Cursor.Default)
                    .transition(Transition.of("background-color", 150.ms))
                    .onClick {
                        if (!interactable) return@onClick
                        confirming = false
                    }
                    .toAttrs()
            ) {
                Text("No")
            }
        }
    }
}
